<template>
	<div class="chat-messages">
		<div v-for="({ messages, senderId }, index) in messageGroups" :key="`message-group-${index}-${senderId}`" :class="['chat-message-group', `is-${senderId === perspectiveUserId ? 'self' : 'recipient'}`]">
			<ProfileAvatar v-if="senderId !== perspectiveUserId" :profile="profiles ? profiles[senderId] : null" size="40"/>
			<div class="flex-col-reverse">
				<div v-for="message of messages" :key="`message-${message.id}`" class="chat-message">
					<div class="chat-message-content" v-html="message.content.text" />
					<div class="chat-message-timestamp">{{ formatMessageTime(message.createdAt, true) }}</div>
				</div>
			</div>
		</div>
		<div class="chat-messages-beginning">{{ beginningText }}</div>
	</div>
</template>

<script>
import { toUserId } from '@/helpers/convert';
import ChatMessage from '@/models/ChatMessage';
import { formatMessageTime } from '@/helpers/format';

export default {
  name: 'ChatMessages',
  model: { prop: 'messages' },
  props: {
    /**
     * The array of chat messages.
     *
     * @type {import('vue/types/options').PropOptions<readonly ChatMessageData[]>}
     */
    messages: {
      type: Array,
      required: true,
    },
    /**
     * The user, profile, or user ID of the user who is viewing the chat messages.
     *
     * @type {import('vue/types/options').PropOptions<UserData | ProfileData | UserId>}
     */
    perspective: {
      default: null,
    },
    /**
     * A dictionary wherein each key is a user ID, and each value is the `Profile` associated
     * with the user ID.
     *
     * @type {import('vue/types/options').PropOptions<Partial<Record<UserId, ProfileData>>>}
     */
    profiles: {
      type: [Object, Array],
      required: true,
    },
  },
  computed: {
    /**
     * @returns {string}
     */
    beginningText() {
      return this.messageGroups.length ? 'Beginning of messages' : 'No messages yet';
    },
    /**
     * @returns {Intl.DateTimeFormat['format']}
     */
    formatDateTime() {
      return this.$getDateTimeFormat({
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format;
    },
    /**
     * @returns {readonly { readonly messages: readonly ChatMessage[]; readonly senderId: UserId }[]}
     */
    messageGroups() {
      /** @type {{ readonly messages: readonly ChatMessage[]; readonly senderId: UserId }[]} */
      const groups = [];
      const { messages } = this;
      if (Array.isArray(messages)) {
        /** @type {ChatMessage[] | undefined} */
        let groupMessages;
        /** @type {ChatMessage} */
        let message;
        /** @type {UserId | undefined} */
        let previousSenderId;
        /** @type {UserId | null | undefined} */
        let senderId;
        for (const data of messages) {
          if (data == null || !(senderId = (message = ChatMessage.from(data)).senderId)) {
            continue;
          }
          if (groupMessages && senderId === previousSenderId) {
            groupMessages.push(message);
          } else {
            Object.freeze(groupMessages);
            groupMessages = [message];
            groups.push(Object.freeze({ messages: groupMessages, senderId }));
            previousSenderId = senderId;
          }
        }
        Object.freeze(groupMessages);
      }
      return Object.freeze(groups);
    },
    /**
     * @returns {UserId | null}
     */
    perspectiveUserId() {
      return toUserId(this.perspective) || this.$activeUser?.id || null;
    },
  },
  methods: {
	formatMessageTime
  },
};
</script>
